/**
 * LanguagePickRows — the Langue rows of B's Pick (kind='language' multi), lot D step 6, UI-SPEC §2.10.
 *
 * The 34 PROMPT_LANGUAGES sorted by French name, filtered accent-insensitively.
 * State right: « {n} lieux » (all places) / « {k} sur {n} lieux » (holes) /
 * « à traduire » (queued, amber) · ✓.
 * - A measured language is checked AND clickable: untick → onRemove (LanguageRemoveDialog).
 * - The ONLY locked row is the source language (the root's defaulted language).
 * - A queued language: untick → onUntick; a new one: tick → onTick.
 */
import { Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { Location } from '@traaker/shared';
import { PROMPT_LANGUAGES } from '@traaker/shared';
import type { Family } from './promptStudioModel';
import { familyPlaces, languageName, languagePlaceCounts, rowLanguage } from './variantModel';
import { Flag } from '../../pages/dashboard/Flag';
import { languageFlag } from '../../pages/dashboard/geoFlags';

export interface LanguagePickRowsProps {
  family: Family;
  locations: Location[];
  /** Brand working language (defaults an unlabelled row's language). */
  workingLanguage: string;
  /** Queued (ticked, not yet saved) languages. */
  queued: string[];
  /** Pick's search text. */
  searchQuery: string;
  onTick: (code: string) => void;
  onUntick: (code: string) => void;
  /** Untick of a MEASURED language: the caller opens LanguageRemoveDialog. */
  onRemove: (code: string) => void;
}

/** Accent-insensitive fold. */
const fold = (s: string) => s.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

const SORTED = [...PROMPT_LANGUAGES].sort((a, b) => a.name.localeCompare(b.name, 'fr'));

export function LanguagePickRows({
  family,
  locations,
  workingLanguage,
  queued,
  searchQuery,
  onTick,
  onUntick,
  onRemove,
}: LanguagePickRowsProps) {
  const { t, i18n } = useTranslation();
  const source = rowLanguage(family.root, workingLanguage);
  const counts = languagePlaceCounts(family, locations, workingLanguage);
  const nPlaces = familyPlaces(family, locations).length;
  const q = fold(searchQuery.trim());

  const rows = SORTED
    .map(({ code }) => ({ code, name: languageName(code, i18n.language) }))
    .filter(({ name }) => !q || fold(name).includes(q));

  if (rows.length === 0) {
    return (
      <Box sx={{ px: 2, py: 1.5, fontSize: 13, color: 'text.disabled' }}>
        {t('promptStudio.pick.noLanguage')}
      </Box>
    );
  }

  return (
    <>
      {rows.map(({ code, name }) => {
        const k = counts.get(code) ?? 0;
        const isSource = code === source;
        const isMeasured = k > 0;
        const isQueued = queued.includes(code);
        const checked = isSource || isMeasured || isQueued;
        const state = isMeasured
          ? k === nPlaces
            ? t('promptStudio.pick.langAll', { count: nPlaces })
            : t('promptStudio.pick.langPartial', { k, n: nPlaces })
          : isQueued
            ? t('promptStudio.pick.toTranslate')
            : '';
        const onClick = () => {
          if (isSource) return;
          if (isMeasured) onRemove(code);
          else if (isQueued) onUntick(code);
          else onTick(code);
        };
        const flag = languageFlag(code);
        return (
          <Box
            key={code}
            component="button"
            type="button"
            disabled={isSource}
            title={isSource ? t('promptStudio.pick.rootLanguage') : undefined}
            onClick={onClick}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              width: '100%',
              px: 2,
              py: 1,
              fontSize: 13,
              textAlign: 'left',
              color: 'text.primary',
              bgcolor: checked ? 'rgba(124,58,237,.06)' : 'transparent',
              border: 'none',
              cursor: isSource ? 'default' : 'pointer',
              opacity: isSource ? 0.6 : 1,
              '&:hover': isSource ? {} : { bgcolor: 'rgba(0,0,0,.04)' },
            }}
          >
            <Box sx={{ width: 18, flexShrink: 0 }}>
              {flag ? <Flag countryCode={flag} size={18} title={name} /> : code.toUpperCase()}
            </Box>
            <Box sx={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</Box>
            {state && (
              <Box
                component="span"
                sx={{ fontSize: 10.5, color: isQueued && !isMeasured ? '#b45309' : 'text.disabled', fontWeight: isQueued && !isMeasured ? 600 : 400 }}
              >
                {state}
              </Box>
            )}
            <Box sx={{ width: 16, fontSize: 16, color: '#7c3aed', flexShrink: 0 }}>{checked ? '✓' : ''}</Box>
          </Box>
        );
      })}
    </>
  );
}
